// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import { removeNullAndUndefinedProps } from "@transformers/object";

import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  CollapsibleWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { Rtw } from "@modules/service/reducer/rtw/types";

import formQuestionsSchema from "./form-questions-schema";
import RTWStepSettings from "./rtw-step-settings";
import StepCompliance from "./step-compliance";
import Step3Form from "./step-form";
import Step4Notifications from "./step-notifications";

type OwnProps = $Exact<{
  defaultStep: 1 | 2 | 3 | 4,
  clientId: number,
  processId: number,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Rtw,
  assignments: Array<HierarchyAssignment>,
}>;

type PropsFromDispatch = $Exact<{
  update: typeof actionCreators.UpdateRtw,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class RtwEditModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    if (this.props.schema) {
      this.validate = new Ajv().compile(this.schema);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validate = new Ajv().compile(this.schema);
    }
  }

  get schema() {
    const { schema } = this.props;
    if (!schema) {
      return null;
    }

    return {
      ...schema,
      properties: {
        ...schema.properties,
        formQuestions: formQuestionsSchema,
      },
    };
  }

  get isValid() {
    if (!this.validate && !this.props.schema) {
      return false;
    }

    const draft = this.props.draft?.content || {};
    const formData = {
      ...removeNullAndUndefinedProps(draft),
      formQuestions: draft.formQuestions, // removeNullAndUndefinedProps func removes nullable properties from questions and they do not get validated so we want to keep the original keys and values
    };

    const validate = this.validate || new Ajv().compile(this.schema);
    const isValid = validate(formData);
    if (process.env.NODE_ENV !== "production" && !isValid) {
      console.warn("isValid errors", validate.errors);
    }

    return isValid;
  }

  handleSubmit = () => {
    if (this.props.draft) {
      this.props.update({
        clientId: this.props.clientId,
        isActive: this.props.isActive,
        processId: this.props.processId,
        draft: this.props.draft,
      });
      this.props.onCancel();
    }
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onCancel}>
          {this.props.t("RETURN_TO_WORK_EDIT_TITLE", { processId: this.props.processId })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <CollapsibleWizard
            defaultStep={this.props.defaultStep}
            data={[
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
            ]}
          >
            <Step
              component={RTWStepSettings}
              title={this.props.t("RETURN_TO_WORK_STEP1")}
              editing
            />
            <Step component={StepCompliance} title={this.props.t("RETURN_TO_WORK_STEP2")} editing />
            <Step component={Step3Form} title={this.props.t("RETURN_TO_WORK_STEP3")} editing />
            <Step
              component={Step4Notifications}
              title={this.props.t("RETURN_TO_WORK_STEP4")}
              editing
            />
          </CollapsibleWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" disabled={!this.isValid} onClick={this.handleSubmit}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getRtwSchema(state),
    draft: serviceSelectors.getRtwDraft(state),
    assignments: serviceSelectors.getRtwAssignments(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  update: actionCreators.UpdateRtw,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(RtwEditModal);
